import { getPostHog, isPostHogEnabled } from "./posthog"
import { extractCentralFromUrl } from "./utils"
import { extractTaxiTeamFromToken, extractCompanyIdFromToken } from "@/utils/tokenUtils"
import type { AnalyticsUserProperties, AnalyticsGroups, BookingCreatedProperties, CompanyInfo } from "./types"

class AnalyticsService {
  private currentGroups: AnalyticsGroups | null = null

  identify(userId: string, properties: Partial<AnalyticsUserProperties>): void {
    if (!isPostHogEnabled()) return
    getPostHog().identify(userId, properties)
  }

  setupUserContext(token: string, username: string, companyInfo: CompanyInfo | null): void {
    if (!isPostHogEnabled()) return

    const central = extractCentralFromUrl()
    const taxiTeam = extractTaxiTeamFromToken(token)
    const companyIdFromToken = extractCompanyIdFromToken(token)
    const companyId = companyInfo?.companyId || companyIdFromToken || "unknown"
    const companyName = companyInfo?.companyName || "unknown"
    const taxiteamCode = taxiTeam?.code || "unknown"
    const taxiteamName = taxiTeam?.name || "unknown"

    this.identify(username, {
      username,
      central,
      taxiteam_code: taxiteamCode,
      taxiteam_name: taxiteamName,
      company_id: companyId,
      company_name: companyName
    })

    this.currentGroups = { central, taxiteam: taxiteamName, company: companyName }

    const posthog = getPostHog()
    posthog.group("central", central, { name: central })
    posthog.group("taxiteam", taxiteamName, { name: taxiteamName, code: taxiteamCode, central })
    posthog.group("company", companyName, { name: companyName, company_id: companyId, taxiteam: taxiteamName, central })
  }

  reset(): void {
    if (!isPostHogEnabled()) return
    this.currentGroups = null
    getPostHog().reset()
  }

  private track(eventName: string, properties?: Record<string, unknown>): void {
    if (!isPostHogEnabled()) return
    getPostHog().capture(eventName, {
      ...properties,
      central: this.currentGroups?.central,
      taxiteam: this.currentGroups?.taxiteam,
      company: this.currentGroups?.company
    })
  }

  trackLogin(): void {
    this.track("user_login", { method: "password" })
  }

  trackLogout(): void {
    this.track("user_logout")
  }

  trackSessionRestored(): void {
    this.track("session_restored")
  }

  trackBookingCreated(properties: BookingCreatedProperties): void {
    this.track("booking_created", properties as unknown as Record<string, unknown>)
  }

  // "Spor taxi" opened, and whether the car's position was there to show
  trackTracking(hadPosition: boolean): void {
    this.track("tracking_opened", { had_position: hadPosition })
  }

  // The taximeter's receipt: opened, printed, shared, or copied where the browser cannot share
  trackReceipt(action: "viewed" | "printed" | "shared" | "copied"): void {
    this.track("receipt_action", { action })
  }

  // Taken back during the countdown, before anything reached the central
  trackOrderUndone(secondsLeft: number): void {
    this.track("order_undone", { seconds_left: secondsLeft })
  }

  // Sent before the undo window ran out, with "Send nå" — together with order_undone, what tells whether the window is
  // too long or too short
  trackOrderSentNow(secondsLeft: number): void {
    this.track("order_sent_now", { seconds_left: secondsLeft })
  }

  trackBookingFailed(statusCode: number | null, errorCode: number | null): void {
    this.track("booking_failed", { status_code: statusCode, error_code: errorCode })
  }

  trackBookingCancelled(bookingNumber: number, statusAtCancel: number): void {
    this.track("booking_cancelled", { booking_number: bookingNumber, status_at_cancel: statusAtCancel })
  }

  trackBookingViewed(bookingNumber: number, status: number): void {
    this.track("booking_viewed", { booking_number: bookingNumber, booking_status: status })
  }

  trackSavedAddressChanged(action: "created" | "updated" | "deleted", source: "form" | "addresses"): void {
    this.track("saved_address_changed", { action, source })
  }

  // "Bruk min posisjon": whether it found an address, and how good the fix was (metres) when there was one
  trackPositionUsed(outcome: "found" | "denied" | "unavailable" | "inaccurate" | "noAddress" | "failed", accuracyMeters: number | null): void {
    this.track("position_used", { outcome, accuracy_m: accuracyMeters })
  }

  trackPageView(pageName: string, routePath: string): void {
    if (!isPostHogEnabled()) return
    getPostHog().capture("$pageview", {
      $current_url: window.location.href,
      page_name: pageName,
      route_path: routePath,
      central: this.currentGroups?.central,
      taxiteam: this.currentGroups?.taxiteam,
      company: this.currentGroups?.company
    })
  }
}

export const analytics = new AnalyticsService()
